import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import ModalChangeIndicator from 'components/modal-change-indicator';
import ModalSendMessage from 'components/modal-send-message';
import AvisoDeCicloForaDaTela from 'components/ui/aviso-de-ciclo-fora-da-tela';
import Button from 'components/ui/button';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import PageHeader from 'components/ui/page-header';
import SeletorDeCiclo from 'components/ui/seletor-de-ciclo';
import { useAuth } from 'context/AuthContext';
import { useCicloDaTela } from 'hooks/useCicloDaTela';
import { useServerTime } from 'hooks/useServerTime';
import { etapasDoCiclo } from 'pages/MinhaEquipe/calendario-do-ciclo';
import CalendarioDoCiclo from 'pages/MinhaEquipe/components/CalendarioDoCiclo';
import { NotFound } from 'pages/NotFound';
import { FOCO_VISIVEL } from 'utils/foco';

import CartaoDaMinhaRealizacao from './components/CartaoDaMinhaRealizacao';
import CartaoDeParticipacao from './components/CartaoDeParticipacao';
import ChamadaDeCadastro from './components/ChamadaDeCadastro';
import FiltroDePeriodo from './components/FiltroDePeriodo';
import PainelDeCicloEncerrado from './components/PainelDeCicloEncerrado';
import PilulasDoCiclo from './components/PilulasDoCiclo';
import SecaoDaLista from './components/SecaoDaLista';
import { AvisoDeFalhaDasExcluidas, CartaoDaRealizacaoExcluida } from './components/SecaoDeExcluidas';
import {
  CicloDoSemestre,
  dicaDoPeriodo,
  ehGestor,
  Entrega,
  estadoDoCadastro,
  faseDaRealizacao,
  faseMensalDe,
  mesesDoCiclo,
  mesPadrao,
  noMes,
  Periodo,
  pilulasDoCiclo,
  podeAtualizarIndicador,
  realizacaoEditavel,
  secoesDoCiclo,
} from './realizacoes-do-semestre';
import { ROTA_DA_APRECIACAO } from './rotas';
import { rostoDoParticipante } from './identidade-do-participante';
import { usePerfil } from './usePerfil';
import { useRascunho } from './useRascunho';
import { useRealizacoesDoSemestre } from './useRealizacoesDoSemestre';

const RAIZ_DA_PAGINA = /^\/realizacao-destaque\/?$/;

/** RDV-03 e RDV-05: título, subtítulo e texto de vazio de cada seção da lista. */
const SECOES = {
  minhas: { titulo: 'Minhas realizações', subtitulo: 'Cadastradas por você', vazio: 'Você não cadastrou realizações neste período.' },
  participacoes: {
    titulo: 'Realizações que participo',
    subtitulo: 'Colegas incluíram você como participante',
    vazio: 'Nenhuma participação neste período.',
  },
  canceladas: { titulo: 'Realizações canceladas', subtitulo: 'Excluídas por você', vazio: 'Nenhuma realização cancelada neste período.' },
};

/** Filtro de período da lista; toda troca de ciclo o devolve a "Ciclo inteiro" (RDV-07, RDV-27). */
interface FiltroDaLista {
  periodo: Periodo;
  mes?: string;
}

const CICLO_INTEIRO: FiltroDaLista = { periodo: 'ciclo' };

interface PedidoDeIndicador {
  id: string;
  owner: string;
}

interface PedidoDeAutorizacao {
  managerName: string;
  participantCount: number;
  taskTitle: string;
  managerId: string;
}

/** Realização Destaque do participante: o ciclo inteiro numa leitura, em três seções, com filtro por mês. */
export default function RealizacaoDestaque() {
  const { user, cyclesExtended, cycles } = useAuth();
  const location = useLocation();
  const rede = !!user?.extra?.rede;
  // RDN-21: a Rede não vê a página; a leitura nem sai.
  const chave = rede ? undefined : user?._id;

  const ciclos = useMemo(
    () => ((cyclesExtended ?? []) as unknown as (CicloDoSemestre & { year?: number })[]).filter(ciclo => (ciclo.year ?? 0) > 2022),
    [cyclesExtended],
  );
  // CIC-10 a CIC-12 (no lugar de RDN-03 antigo): o ciclo da página é o ciclo do app; trocar aqui troca o do app.
  const { ciclo, foraDaTela, maisRecente, trocar } = useCicloDaTela(ciclos);
  const cicloId = ciclo?._id;

  const { tarefas, carregando, erro, recarregar, excluidas } = useRealizacoesDoSemestre(chave, ciclo);
  const { perfil, reler: relerPerfil } = usePerfil(chave);
  const { realDatetime: agora, hasTime } = useServerTime();

  const [filtro, setFiltro] = useState<FiltroDaLista>(CICLO_INTEIRO);
  // RDV-07 e RDV-27: qualquer troca de ciclo (seletor, menu, "Voltar ao ciclo atual") zera o filtro, ainda no
  // mesmo render, para que voltar a um ciclo não restaure o "Mês" escolhido antes.
  const [cicloDoFiltro, setCicloDoFiltro] = useState(cicloId);
  if (cicloDoFiltro !== cicloId) {
    setCicloDoFiltro(cicloId);
    setFiltro(CICLO_INTEIRO);
  }
  // RDV-15: cada cartão informa a sua pílula (RDV-14); as do topo contam o ciclo inteiro, escondidos inclusive.
  const [pilulaPorId, setPilulaPorId] = useState<Record<string, string>>({});
  const guardarPilula = useCallback(
    (id: string, pilula: string) => setPilulaPorId(atual => (atual[id] === pilula ? atual : { ...atual, [id]: pilula })),
    [],
  );
  const [aviso, setAviso] = useState('');
  const [pedidoDeIndicador, setPedidoDeIndicador] = useState<PedidoDeIndicador>();
  const [pedidoDeAutorizacao, setPedidoDeAutorizacao] = useState<PedidoDeAutorizacao>();

  // RDN-20: ao fechar o cadastro/edição (`:id` no Outlet), a página volta à raiz e relê o ciclo.
  const naRaiz = RAIZ_DA_PAGINA.test(location.pathname);
  const estavaNaRaiz = useRef(naRaiz);
  const paginaRef = useRef<HTMLDivElement>(null);
  const tituloRef = useRef<HTMLHeadingElement>(null);
  // O foco volta ao botão do topo que abre o cadastro; sem ele, ao `h1`. Vale no retorno e de novo quando a
  // releitura termina, porque um cadastro novo troca "Cadastrar" por "Editar" e o botão focado sai da tela.
  const focoAposReleitura = useRef<'inicio' | 'fim' | null>(null);
  const focarTopo = useCallback(() => {
    const botaoDoCadastro = paginaRef.current?.querySelector<HTMLElement>('[data-abre-cadastro]');
    (botaoDoCadastro ?? tituloRef.current)?.focus();
  }, []);
  useEffect(() => {
    if (naRaiz && !estavaNaRaiz.current) {
      recarregar();
      focarTopo();
      focoAposReleitura.current = 'inicio';
    }
    estavaNaRaiz.current = naRaiz;
  }, [naRaiz, recarregar, focarTopo]);
  useEffect(() => {
    if (focoAposReleitura.current === 'inicio' && carregando) focoAposReleitura.current = 'fim';
    else if (focoAposReleitura.current === 'fim' && !carregando) {
      focoAposReleitura.current = null;
      focarTopo();
    }
  });

  const gestor = ehGestor(perfil);
  const { minhas, participacoes, excluidas: canceladas } = useMemo(
    () => secoesDoCiclo(tarefas, excluidas?.itens ?? [], chave ?? ''),
    [tarefas, excluidas, chave],
  );
  const fases = ciclo?.steps;
  const esperandoLista = carregando && !tarefas.length;
  // RDV-06: o gestor vê só as participações (RDN-40); os chips contam só o que ele vê.
  const cartoesDoCiclo = gestor ? participacoes : [...minhas, ...participacoes, ...canceladas];
  const { periodo } = filtro;
  // RDV-10: sem chip escolhido, vale o mês padrão, calculado a cada renderização: antes da hora do servidor `agora` é 0
  // e nenhum mês começou; quando ela chega, o chip da fase aberta fica marcado.
  const mesEscolhido = periodo === 'mes' ? (filtro.mes ?? mesPadrao(fases, agora)?._id) : undefined;
  const cicloAberto = ciclos.find(item => item.status === 'open');
  const falhou = erro || perfil === null;
  // RDV-35: o carregamento fica no lugar das seções até chegarem o perfil e a lista; "Realizações canceladas" espera
  // `find/entregas_excluidas` sozinha, dentro da própria seção.
  const carregandoSecoes = perfil === undefined || esperandoLista;
  // RDV-42: as pílulas de aguardando só aparecem quando todos os cartões do ciclo já informaram a sua.
  const pilulasProntas = hasTime && [...minhas, ...participacoes].every(realizacao => realizacao._id in pilulaPorId);
  const podeCadastrar = user?.extra?.task !== false && !perfil?.visualizador;

  const estado =
    hasTime && perfil && !gestor && !esperandoLista && !erro
      ? estadoDoCadastro({
          ciclos,
          cicloSelecionadoId: cicloId,
          minhas,
          agora,
          podeCadastrar,
          editavel: realizacao => realizacaoEditavel(realizacao, faseMensalDe(realizacao.created, fases), chave, agora),
        })
      : null;
  const temRascunho = useRascunho(estado?.tipo === 'aberto-sem-realizacao');

  if (rede) return <NotFound />;
  if (!user) return null;
  // RDN-40/RDN-52: o gestor vê aqui as participações; quem chega da fila (`?vista=participacoes`) volta para ela.
  const veioDaFila = new URLSearchParams(location.search).get('vista') === 'participacoes';

  function trocarDeCiclo(id: string) {
    setAviso('');
    trocar(id);
  }

  function tentarDeNovo() {
    if (perfil === null) relerPerfil();
    recarregar();
  }

  function participacaoConcluida(texto: string) {
    setAviso(texto);
    recarregar();
  }

  function escolherPeriodo(novo: Periodo) {
    // RDV-10: "Mês" marca o mês padrão (`mesEscolhido`) até o funcionário escolher outro chip.
    setFiltro({ periodo: novo });
  }

  /**
   * RDV-11: o chip de mês esconde, sem desmontar, os cartões de outro mês; assim a pílula de cada um segue valendo
   * (RDV-15). Sem chip marcado (ciclo inteiro, ou "Mês" sem mês iniciado, RDV-41), todos ficam à vista.
   */
  const visivel = (created: number) => !mesEscolhido || noMes(created, mesEscolhido, fases);

  function renderizarMinha(realizacao: Entrega) {
    const fase = faseMensalDe(realizacao.created, fases);
    return (
      <CartaoDaMinhaRealizacao
        data={realizacao}
        fase={fase}
        rotuloDaFase={faseDaRealizacao(realizacao.created, fases)}
        playerInfo={perfil}
        editavel={realizacaoEditavel(realizacao, fase, chave, agora)}
        podeAtualizarIndicador={podeAtualizarIndicador(realizacao, fase, chave)}
        onAtualizarIndicador={() => setPedidoDeIndicador({ id: realizacao._id, owner: realizacao.owner })}
        onSolicitarAutorizacao={setPedidoDeAutorizacao}
        onRecarregar={recarregar}
        onPilula={pilula => guardarPilula(realizacao._id, pilula)}
      />
    );
  }

  function renderizarParticipacao(realizacao: Entrega) {
    return (
      <CartaoDeParticipacao
        data={realizacao}
        fase={faseMensalDe(realizacao.created, fases)}
        rotuloDaFase={faseDaRealizacao(realizacao.created, fases)}
        playerInfo={perfil}
        onConcluida={participacaoConcluida}
        onRecarregar={recarregar}
        onPilula={pilula => guardarPilula(realizacao._id, pilula)}
      />
    );
  }

  function renderizarSecao<T extends { _id: string; created: number }>(
    secao: keyof typeof SECOES,
    itens: T[],
    renderizar: (item: T) => JSX.Element,
    falha?: JSX.Element,
  ) {
    return (
      <SecaoDaLista {...SECOES[secao]} quantidade={falha ? 0 : itens.filter(item => visivel(item.created)).length} falha={falha}>
        {itens.map(item => (
          <div key={item._id} hidden={!visivel(item.created)}>
            {renderizar(item)}
          </div>
        ))}
      </SecaoDaLista>
    );
  }

  function renderizarLista() {
    if (foraDaTela) return <AvisoDeCicloForaDaTela ciclo={foraDaTela} disponivel={maisRecente} onVer={trocarDeCiclo} />;

    if (falhou) {
      return (
        <div className="flex flex-wrap items-center gap-4 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 flex-1 text-[13px] text-content">Não foi possível carregar as realizações deste ciclo.</p>
          <Button variant="solido" onClick={tentarDeNovo}>
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (carregandoSecoes) return <CartoesCarregando />;
    if (gestor) return renderizarSecao('participacoes', participacoes, renderizarParticipacao);

    return (
      <>
        {renderizarSecao('minhas', minhas, renderizarMinha)}
        {renderizarSecao('participacoes', participacoes, renderizarParticipacao)}
        {renderizarSecao(
          'canceladas',
          canceladas,
          realizacao => (
            <CartaoDaRealizacaoExcluida
              realizacao={realizacao}
              autor={{ chave: chave ?? '', nomeGuerra: perfil?.nome_guerra ?? user?.name, rosto: rostoDoParticipante(user) }}
              rotuloDaFase={faseDaRealizacao(realizacao.created, fases)}
            />
          ),
          !excluidas ? <CartoesCarregando /> : excluidas.falhou ? <AvisoDeFalhaDasExcluidas /> : undefined,
        )}
      </>
    );
  }

  return (
    <>
      <div ref={paginaRef} className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
        <PageHeader ref={tituloRef} titulo="Realização Destaque" voltar={{ to: '/', destino: 'a página inicial' }} />

        {perfil === undefined ? null : gestor ? (
          <div className="flex flex-wrap items-center gap-3.5 rounded-jj-lg bg-brand-green px-5 py-3.5">
            <p className="m-0 flex-[1_1_340px] text-[12.5px] leading-[1.55] text-brand-navy">
              Você está vendo as Realizações Destaque em que foi incluído como <b>participante</b>.
            </p>
            <Link
              to={ROTA_DA_APRECIACAO}
              className={`ml-auto inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-jj-sm border border-brand-blue bg-brand-white px-[18px] py-2.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue no-underline hover:text-brand-blue-dark hover:no-underline focus:text-brand-blue ${FOCO_VISIVEL}`}
            >
              {veioDaFila && (
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              )}
              {veioDaFila ? 'Voltar para apreciação' : 'Ir para a apreciação'}
            </Link>
          </div>
        ) : (
          <ChamadaDeCadastro
            estado={estado}
            agora={agora}
            temRascunho={temRascunho}
            onIrParaCicloAtual={trocarDeCiclo}
          />
        )}

        {!gestor && !foraDaTela && !falhou && !carregandoSecoes && (
          <PilulasDoCiclo pilulas={pilulasProntas ? pilulasDoCiclo(minhas, participacoes, pilulaPorId) : undefined} />
        )}

        <FiltroDePeriodo
          periodo={periodo}
          onPeriodo={escolherPeriodo}
          dica={ciclo ? dicaDoPeriodo(periodo, ciclo) : ''}
          meses={hasTime ? mesesDoCiclo(fases, cartoesDoCiclo, agora) : undefined}
          mesEscolhido={mesEscolhido}
          onMes={mes => setFiltro({ periodo: 'mes', mes })}
        >
          <div className="min-w-[180px] flex-[0_1_200px]">
            <label
              htmlFor="rd-ciclo"
              className="mb-[7px] block text-[11px] font-bold uppercase tracking-[0.05em] text-content-soft"
            >
              Ciclo
            </label>
            <SeletorDeCiclo ciclos={ciclos} valor={cicloId} onChange={trocarDeCiclo} rotulo="Ciclo" inputId="rd-ciclo" />
          </div>
        </FiltroDePeriodo>

        {ciclo?.status === 'closed' && cicloAberto && !foraDaTela && (
          <PainelDeCicloEncerrado ciclo={ciclo.title} onVoltar={() => trocarDeCiclo(cicloAberto._id)} />
        )}

        <p role="status" className="m-0 mt-3.5 text-xs font-bold text-brand-blue-dark empty:mt-0">
          {aviso}
        </p>
        <div className="mt-3.5">{renderizarLista()}</div>

        {ciclo?.status === 'open' && hasTime && <CalendarioDoCiclo etapas={etapasDoCiclo(cycles)} agora={agora} />}
      </div>

      {pedidoDeIndicador && (
        <ModalChangeIndicator
          id={pedidoDeIndicador.id}
          owner={pedidoDeIndicador.owner}
          callback={alterou => {
            setPedidoDeIndicador(undefined);
            if (alterou) recarregar();
          }}
        />
      )}

      {pedidoDeAutorizacao && (
        <ModalSendMessage {...pedidoDeAutorizacao} onClose={() => setPedidoDeAutorizacao(undefined)} />
      )}

      <Outlet />
    </>
  );
}
